import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HeartPulse, Loader2, Lock } from "lucide-react";
import { CardBody, FamilyBody, SevereBadge, Tel } from "@/components/emergency/parts";
import { HouseBody } from "@/components/emergency/House";
import { GuestPets } from "@/components/pets/GuestPets";
import { ShutoffBody } from "@/pages/Shutoffs";
import { usePinTheme } from "@/context/LookContext";

function GuestImg({ session, url, alt, className, testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => { axios.get(url, { headers: { "X-Sitter-Session": session }, responseType: "blob" }).then(({ data }) => setSrc(URL.createObjectURL(data))).catch(() => setSrc("")); }, [url, session]);
  return src ? <img src={src} alt={alt} className={className} data-testid={testid} /> : <div className={`${className} bg-q-raised`} />;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sitter`;
const EXPIRED = "This link has expired. Ask the family for a new one.";

export default function SitterView() {
  const { token } = useParams();
  const skey = `sitter:${token.slice(0, 16)}`;
  const [session, setSession] = useState(() => localStorage.getItem(skey));
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  usePinTheme(state === "view" ? "cream" : "dark");
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
  const load = useCallback(async (s) => {
    if (!s) { setState("code"); return; }
    try { const { data } = await axios.get(`${API}/${token}/view`, { headers: { "X-Sitter-Session": s } }); setD(data); setState("view"); } catch (e) {
      if (e.response?.status === 410) setState("expired");
      else { localStorage.removeItem(skey); setSession(null); setState("code"); }
    }
  }, [token, skey]);
  useEffect(() => { load(session); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (state !== "view") return undefined;
    const t = setInterval(() => load(session), 30000);
    return () => clearInterval(t);
  }, [state, session, load]);
  const pdf = async (file = "card.pdf", name = "emergency-card.pdf", fail = "Couldn't download the card") => {
    try {
      const { data } = await axios.get(`${API}/${token}/${file}`, { headers: { "X-Sitter-Session": session }, responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setMsg(fail); }
  };
  const unlock = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try {
      const { data } = await axios.post(`${API}/${token}/unlock`, { code });
      localStorage.setItem(skey, data.session); setSession(data.session); await load(data.session);
    } catch (x) {
      const st = x.response?.status;
      if (st === 410) setState("expired"); else setMsg(x.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); setCode(""); }
  };
  const shell = (children) => <div className="min-h-screen bg-q-bg px-4 py-6 text-q-text" data-testid="sitter-view"><div className="mx-auto max-w-md space-y-4">{children}</div></div>;
  if (state === "loading") return shell(<p className="pt-20 text-center text-q-muted"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></p>);
  if (state === "expired") return shell(<p className="q-card mt-16 text-center font-semibold" data-testid="sitter-expired">{EXPIRED}</p>);
  if (state === "code") return shell(
    <form onSubmit={unlock} className="q-card mt-12 space-y-4" data-testid="sitter-code-form">
      <Lock className="mx-auto h-8 w-8 text-[rgb(var(--glow-family))]" />
      <h1 className="text-center font-display text-2xl font-extrabold">Enter the 6-digit code</h1>
      <input className="q-input w-full text-center text-2xl tracking-[0.4em]" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="sitter-code-input" />
      {msg && <p className="text-center text-sm font-semibold text-[rgb(248,113,113)]" data-testid="sitter-code-error">{msg}</p>}
      <button className="q-btn q-btn-primary w-full" disabled={busy || code.length !== 6} data-testid="sitter-code-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Open</button>
    </form>,
  );
  return shell(<>
    <header>
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[rgb(var(--glow-family))]">{d.hub_name} · {d.type} · {d.name}</p>
      <h1 className="font-display text-3xl font-extrabold">Guest pass</h1>
    </header>
    <section className="q-card flex flex-wrap gap-x-6" data-testid="sitter-fixed">
      {d.fixed.map((f) => <p key={f.label} className="font-bold">{f.label}: <Tel phone={f.phone} testid={`sitter-fixed-${f.label.replace(/\s+/g, "-").toLowerCase()}`} /></p>)}
    </section>
    {d.parents.length > 0 && (
      <section className="q-card" data-testid="sitter-parents">
        <h2 className="mb-2 font-display font-extrabold">Parents</h2>
        {d.parents.map((p, n) => <p key={n} className="flex flex-wrap items-center gap-x-2 text-sm">{p.name} <Tel phone={p.phone} testid={`sitter-parent-phone-${n}`} /></p>)}
      </section>
    )}
    {d.shutoffs && d.shutoffs.length > 0 && <section className="space-y-3" data-testid="sitter-shutoffs"><h2 className="font-display text-xl font-extrabold">Shutoffs</h2>
      {d.shutoffs.map((c) => <details key={c.id} className="q-card" data-testid={`sitter-shutoff-${c.kind}`}><summary className="flex min-h-[44px] cursor-pointer items-center font-semibold">{c.name}</summary>
        <div className="mt-3"><ShutoffBody c={c} photoUrl={(p) => `${API}/${token}/shutoffs/${c.id}/photos/${p.id}`} Img={(props) => <GuestImg session={session} {...props} />} /></div></details>)}</section>}
    {d.away && <section className="q-card space-y-3" data-testid="sitter-away"><h2 className="font-display text-xl font-extrabold">Away checklist</h2>
      <ol className="list-decimal space-y-1 pl-5 text-sm">{d.away.away.map((i) => <li key={i.title}>{i.title} <span className="opacity-70">· {i.room}</span></li>)}</ol>
      <div className="flex flex-wrap gap-2">{[["list", "Sign-off sheet (PDF)"], ["rooms", "Room by room (PDF)"]].map(([l, t]) => (
        <button key={l} className="q-btn q-btn-secondary min-h-[44px]" onClick={() => axios.get(`${API}/${token}/away.pdf?layout=${l}`, { headers: { "X-Sitter-Session": session }, responseType: "blob" }).then(({ data }) => window.open(URL.createObjectURL(data), "_blank"))} data-testid={`sitter-away-${l}`}>{t}</button>))}</div>
    </section>}
    {d.house_info && <section className="q-card" data-testid="sitter-house"><h2 className="mb-2 font-display font-extrabold">House info</h2><HouseBody info={d.house_info} /></section>}
    {(d.care || []).map((c) => <button key={c.id} className="q-btn q-btn-secondary min-h-[44px] w-full" onClick={() => pdf(`care/${c.id}/card.pdf`, "care-emergency-card.pdf")} data-testid={`sitter-care-card-${c.id}`}>Emergency card: {c.name} (PDF)</button>)}
    {d.card && <button className="q-btn q-btn-primary min-h-[44px] w-full" onClick={() => pdf()} data-testid="sitter-card-download">Download emergency card (PDF)</button>}
    {d.card && <button className="q-btn q-btn-secondary min-h-[44px] w-full" onClick={() => pdf("sheet.pdf", "sitter-sheet.pdf", "Couldn't download the sitter sheet")} data-testid="sitter-sheet-download">Download sitter sheet (PDF)</button>}
    {d.pets && d.pets.length > 0 && <GuestPets api={`${API}/${token}`} session={session} d={d} reload={() => load(session)} />}
    {d.kids.map((k) => (
      <section key={k.key} className="q-card" data-testid={`sitter-kid-${k.name}`}>
        <div className="mb-3 flex flex-wrap items-center gap-2"><HeartPulse className="h-5 w-5 text-[rgb(var(--glow-family))]" /><h2 className="flex-1 font-display text-xl font-extrabold">{k.name}</h2>{k.card.severe.length > 0 && <SevereBadge testid={`sitter-severe-${k.name}`} />}</div>
        <CardBody card={k.card} keyId={k.name} sitter />
      </section>
    ))}
    {d.family && <section className="q-card" data-testid="sitter-family"><h2 className="mb-2 font-display font-extrabold">Family</h2><FamilyBody family={d.family} /></section>}
    <p className="pb-6 text-center text-xs text-q-faint">Read-only · until {new Date(d.expires_at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })}</p>
  </>);
}
